import type {
    ComponentPropsWithoutRef,
    ComponentPropsWithRef,
    ReactElement,
    ElementType,
    ComponentProps,
} from "react";
import React, { forwardRef } from "react";

import type { OmitOfUnion } from "../../../types/react";

export interface AsProp<C extends ElementType = ElementType> {
    as: C;
}

/** Получаем Ref переданного ElementType */
type BoxRef<T extends ElementType> = ComponentPropsWithRef<T>["ref"];

type BoxReturnType = ReactElement | null;

/**
 * Все Box'ы должны иметь displayName
 */
type AdditionalBoxComponentProps = {
    displayName: string;
};

/**
 * У компонента может быть union в типах, который разрушает дефолтный Omit
 */
type AllBoxProps<
    C extends ElementType,
    P,
    OmitKeys extends keyof ComponentProps<C> = never,
> = OmitOfUnion<ComponentProps<C>, keyof AsProp<C> | keyof P | OmitKeys> & {
    ref?: BoxRef<C>;
};

/** Получаем пропсы из переданного ElementType с обязательным as */
type BoxProps<C extends ElementType, P> = AsProp<C> & AllBoxProps<C, P>;

/** Получаем пропсы из переданного ElementType без обязательного as */
type BoxPropsWithDefault<
    C extends ElementType,
    P,
    OmitKeys extends keyof ComponentProps<C>,
> = Partial<AsProp<C>> & AllBoxProps<C, P, OmitKeys>;

/**
 * Интерфейс для Box-компонента с обязательным as
 */
export type BoxComponent<P = unknown> = <C extends ElementType>(
    props: P & BoxProps<C, P>,
) => BoxReturnType;

/**
 * Интерфейс для Box-компонента, у которого есть какой-то компонент для as по-умолчанию
 */
export type BoxComponentWithDefault<
    Default extends ElementType,
    P = unknown,
    OmitKeys extends keyof (ComponentPropsWithoutRef<Default> & P) = never,
> = <C extends ElementType = Default>(
    props: P & BoxPropsWithDefault<C, P, OmitKeys>,
) => BoxReturnType;

/**
 * Получаем значение аргумента props из нашего Box-компонента
 */
export type BoxArgPropsType<T> = T extends (props: infer P) => BoxReturnType
    ? P
    : never;

type BoxForwardRefRenderFunction<
    C extends BoxComponent<any> | BoxComponentWithDefault<any, any>,
> = (
    /**
     * То что пропсы могут иметь интерфейс "... Omit<any, 'as'>" - абсолютно нормально, так как мы не знаем заранее, что нам передадут.
     * Из-за этой особенности extractProps не сможет сузить правильные типы, поэтому все выходные пропсы будут иметь тип any.
     */
    props: BoxArgPropsType<C>,
    ref: BoxArgPropsType<C>["ref"],
) => BoxReturnType;

/**
 * Создаём обёртку для box-компонентов с корректными типами
 */
export const boxForwardRef = <
    C extends BoxComponent<any> | BoxComponentWithDefault<any, any>,
>(
    Component: BoxForwardRefRenderFunction<C>,
) => forwardRef(Component) as unknown as C & AdditionalBoxComponentProps;

export const box = <
    C extends BoxComponent<any> | BoxComponentWithDefault<any, any>,
>(
    Component: C,
) => Component as typeof Component & AdditionalBoxComponentProps;

/**
 * Компонент, реализующий динамические теги. С его помощью можно получить любой реакт-тег, в том числе и компоненты,
 * со всеми принадлежащими ему пропсами.
 * @constructor
 * @author alexander.gordeev@vk.team
 */
export const Box = boxForwardRef<BoxComponent>(
    ({ as: Element, ...props }, ref) => {
        // Если компоненту нужно будет, он сам это запросит
        // eslint-disable-next-line ok-custom-rules/check-attribute
        return <Element {...props} ref={ref} />;
    },
);
